import { Jsonified } from "@redotech/json/json";
import { LoyaltyAutocheck } from "@redotech/redo-loyalty-common/loyalty-autocheck";
import { DeepPartial } from "@redotech/util/type";
import * as z from "zod/v4";
import { BrandKit } from "./brand-kit";
import { CartProductType } from "./cart-product-types";
import { CartOptimizationSettings } from "./cart/cart-optimization-settings";
import { ConciergeProductFormSettings } from "./concierge-product-form/concierge-product-form";
import { ConversionAutocheck } from "./conversion/conversion-autocheck";
import { Coverage, PaymentModel } from "./coverage";
import { CoverageMlModelDb } from "./coverage-calculator/coverage-ml/registry";
import type {
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "./extended-warranty-enable-criteria";
import type { AttachmentStrategy } from "./returns/attachment-strategy";
import { SaveForLaterSlideInPanelAnchorLocation } from "./slide-in-panel/slide-in-panel-anchor-location";
import { TreatmentDoc } from "./split";
import {
  CheckoutUICustomizationSettings,
  Icons,
} from "./storefront/checkout-ui-customization";
import type {
  Address,
  Automation,
  CoverageCreditIncentive,
  DynamicReturnPricing,
  InfoModalVersion,
  Portal,
  Settings,
  ShopperFacingBillingInfo,
  TnplWidgetSettings,
} from "./team";
import {
  BackInStockInsertionMode,
  HideRedoConfig,
  InternationalReturnPricing,
  PricingRuleSet,
  ShopperFacingBillingStatus,
  StorefrontConfig,
  ToggleFields,
} from "./team";
import { TnplContractTemplateInAutoCheck } from "./tnpl-contract-template";
export interface CoverageInfo {
  returns: {
    cartEligible: boolean;
    price: number;
    isFinalSale: boolean;
    reasonIneligible?: string;
  };
  packageProtection: {
    cartEligible: boolean;
    price: number;
    reasonIneligible?: string;
  };
  both: { cartEligible: boolean; price: number; reasonIneligible?: string };
}
export enum InsertionPosition {
  AFTER = "after",
  BEFORE = "before",
  PREPEND = "prepend",
  APPEND = "append",
}

export const insertionPositionToDisplayText = {
  [InsertionPosition.AFTER]: "After",
  [InsertionPosition.BEFORE]: "Before",
  [InsertionPosition.PREPEND]: "Prepend",
  [InsertionPosition.APPEND]: "Append",
};

export enum WidgetElementType {
  COVERAGE_CHECKOUT_BUTTON = "coverage-checkout-button",
  NON_COVERAGE_CHECKOUT_BUTTON = "non-coverage-checkout-button",
  BOTH_CHECKOUT_BUTTONS = "both-checkout-buttons",
  INFO_CARD = "info-card",
  SINGLE_TOGGLE = "single-toggle",
  DOUBLE_TOGGLE = "double-toggle",
  CART_CARD = "cart-card",
  CHECKOUT_BUTTON = "checkout-button",
}

export enum PlacementType {
  PDP = "Product Page",
  CART = "Cart",
}

export enum ButtonSettingPerProduct {
  BOTH = "both",
  RETURN = "return",
  PACKAGE_PROTECTION = "packageProtection",
}

export const buttonSettingPerProductToDisplayText: Record<
  ButtonSettingPerProduct,
  string
> = {
  [ButtonSettingPerProduct.BOTH]: "Returns & package protection",
  [ButtonSettingPerProduct.RETURN]: "Returns only",
  [ButtonSettingPerProduct.PACKAGE_PROTECTION]: "Package protection only",
};

export interface TitleSubtitle {
  titleText?: string;
  subtitleText?: string;
}
export interface CoverageText {
  returns?: TitleSubtitle;
  packageProtection?: TitleSubtitle;
  both?: TitleSubtitle;
}

export interface NonCoverageButtonText {
  returns?: string;
  packageProtection?: string;
  both?: string;
}

export interface CoverageButtonStyles {
  color?: string;
  fontColor?: string;
  subtitleFontColor?: string;
  fontSize?: number;
  subtitleFontSize?: number;
  nonCoverageFontColor?: string;
  nonCoverageFontSize?: number;
  includeNonCoverageUnderline?: boolean;
}

export interface InfoCardStyles {
  color?: string;
  titleFontSize?: number;
  subtitleFontSize?: number;
  titleFontColor?: string;
  subtitleFontColor?: string;
}

interface BaseWidgetConfig {
  elementType: WidgetElementType;
  name?: string;
  placementSelector: string;
  placementType?: PlacementType;
  insertionPosition?: InsertionPosition;
  shadowRootSelector?: string;
  placeInsideAndOutsideShadowRoot?: boolean;
  dataTarget?: string;
  localizationId?: string;
}

export interface IndividualCheckoutButtons extends BaseWidgetConfig {
  elementType:
    | WidgetElementType.COVERAGE_CHECKOUT_BUTTON
    | WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON;
  placementSelector: string;
  content: string; // HTML, with template variables
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
}

export interface BothCheckoutButtons extends BaseWidgetConfig {
  elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS;
  placementSelector: string;
  coverageButtonText?: CoverageText;
  nonCoverageButtonText?: NonCoverageButtonText;
  buttonSettingPerProduct?: ButtonSettingPerProduct;
  coverageButtonStyles?: CoverageButtonStyles;
  includeNonCoverageUnderline?: boolean;
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
}

export enum ImageElementType {
  NONE = "none",
  CUSTOM_IMAGE = "custom-image",
  SHIELD = "shield",
  GIFT = "gift",
  LOCK = "lock",
}

export enum PriceLocation {
  NONE = "none",
  TITLE = "title",
  RIGHT = "right",
}

export const priceLocationToDisplayText = {
  [PriceLocation.NONE]: "None",
  [PriceLocation.TITLE]: "Title",
  [PriceLocation.RIGHT]: "Right",
};

export interface InfoCard extends BaseWidgetConfig {
  elementType: WidgetElementType.INFO_CARD;
  placementSelector: string;
  cardText?: CoverageText;
  showInfoIcon: boolean;
  badgeWord?: string;
  infoCardStyles?: InfoCardStyles;
  imageElement?: ImageElementType;
  priceLocation?: PriceLocation;
  showBackground?: boolean;
  infoButtonCustomAttributes?: Record<string, string>;
}

export function getCardStyleLabel(showBackground: boolean) {
  return showBackground ? "Card with background" : "Inline card";
}

export interface SingleToggleWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.SINGLE_TOGGLE;
}

export interface DoubleToggleWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.DOUBLE_TOGGLE;
}

export interface CartCardWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.CART_CARD;
}

export interface CheckoutButtonWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.CHECKOUT_BUTTON;
}

export type WidgetConfig =
  | InfoCard
  | BothCheckoutButtons
  | IndividualCheckoutButtons
  | SingleToggleWidgetConfig
  | DoubleToggleWidgetConfig
  | CartCardWidgetConfig
  | CheckoutButtonWidgetConfig;

export interface TermsAndConditions {
  blockCheckoutIfNotAccepted: boolean;
  termsCheckboxSelector?: string;
  blockedMessageText?: string;
}

export interface ClickStandardCheckoutButton {
  enabled: boolean;
  checkoutButtonSelector: string;
}

export interface InfoModalSettingsValue {
  cartIconEnabled: boolean;
  checkoutIconEnabled: boolean;
  enableTopLayerModalApi: boolean;
  logo: string;
  sideImage: string;
  version: InfoModalVersion;
  headerText: string;
  subheaderText: string;
  line1: { titleText: string; subtitleText: string };
  line2: { titleText: string; subtitleText: string };
  line3: { titleText: string; subtitleText: string };
  line4?: { titleText: string; subtitleText: string };
  customCss: string;
}

export type InfoModalSettingsValueFourLines = Exclude<
  InfoModalSettingsValue,
  "line4"
> & { line4: { titleText: string; subtitleText: string } };

export type InfoModal = DeepPartial<InfoModalSettingsValue>;

export enum StorefrontIpGeolocationProvider {
  IPIFY = "ipify",
  STOREFRONT_EMBED_SERVER = "storefront-embed-server",
}

export enum CartValueCalculationMethod {
  POST_DISCOUNT = "cart_value_post_discount",
  PRE_DISCOUNT = "cart_value_pre_discount",
}

export interface WidgetAutocheck {
  teamId?: string;
  widgetSlug?: string;
  ipGeolocationProvider?: StorefrontIpGeolocationProvider;
  noDebounceButtonRendering?: boolean;
  exchangeBannerPositionBottom?: boolean;
  exchangeBannerInsertBeforeBody?: boolean;
  icon?: Icons;
  iconColor?: string;
  iconBackgroundColor?: string;
  displayIcon: boolean;
  storefrontAccessToken: string;
  multistorefront?: StorefrontConfig[];
  html?: string | null;
  checkboxText?: string | null;
  logo?: string | null;
  enableTopLayerModalApi?: boolean;
  toggleTextOptions?: {
    returnToggle?: ToggleFields;
    packageProtectionToggle?: ToggleFields;
    bothProductToggle?: ToggleFields;
  };
  groupNearbyToggleElementsTogether?: boolean;
  checkboxTextAfterRedo?: string | null;
  checkoutSelector?: string;
  exchangeBannerColor?: string;
  exchangeBannerText?: string;
  exchangeBannerFont?: string;
  exchangeBannerFontColor?: string;
  exchangeBannerShowDoneButton: boolean;
  exchangeBannerCustomCss?: string;
  disableRedoForCustomer: boolean;
  coverageExcludeCollections: string[];
  coverageExcludeDiscountCodes: string[];
  coverageExcludeProperties: string[];
  coverageExcludedVariantMetafields?: string[];
  excludeCoverageIfAnyInvalidProduct?: boolean;
  locationBasedAutocheckRemoval?: { enabled: boolean };
  redoAutoCheck: boolean;
  selector: string;
  paidModel: PaymentModel;
  textColor: string;
  coverage: Coverage;
  /**
   * This boolean represents whether we should sell returns coverage to items that are eligible for returns.
   */
  coverageEnabled: boolean;
  enabled: boolean;
  finalSaleTags: string[];
  hideCheckboxBranding: boolean;
  hideCheckbox: boolean;
  hideCheckboxPrice: boolean;
  countries: string[];
  countriesPackageProtection: string[];
  countriesFinalSaleReturns: string[];
  submitButtonSelector?: string;
  modalTextAdjustments: {
    text1: string;
    title1: string;
    text2: string;
    title2: string;
    text3: string;
    title3: string;
  };
  infoModal?: InfoModal;
  modalLogo?: string;
  shopSiteUseCartDiscounts?: boolean;
  excludeProductTags?: string[];
  cartValueCalculationMethod?: CartValueCalculationMethod;
  excludedItemProperties?: { name: string; value: string }[];
  cartToggle: {
    enabled: boolean;
    useShadowRootInCart?: boolean;
    cartShadowRootSelector?: string;
    cartToggleSelector?: string;
    cartToggleShadowRootSelector?: string;
    cartClickListenerSelector?: string;
    toggleOnColor: string;
    toggleOffColor: string;
    toggleCircleColor: string;
    cartTogglePlacement?: InsertionPosition;
    cartReplaceItems?: boolean;
  };
  widgetConfigs?: WidgetConfig[];
  hideRedoProduct?: HideRedoConfig;
  checkoutExtensionEnabled: boolean;
  checkoutAnalyticsInCheckoutToggleWidgetEnabled?: boolean;
  checkboxFontSize?: number;
  toggleFontSize?: number;
  infoIconLink?: string;
  attachmentStrategy: AttachmentStrategy;
  toggleSubtextEnabled: boolean;
  packageProtectionAutoCheck: boolean;
  protectionExcludeTags?: string[];
  usingCartAndCheckoutToggle?: boolean;
  forceDoubleCheckoutToggle?: boolean;
  setCheckoutToggleBasedOnOptInCartAttribute?: boolean;
  hideCheckoutUiWhenOptedIn?: boolean;
  openShadowRoot?: boolean;
  packageProtection: {
    /**
     * This boolean represents whether we should sell package protection to items that are eligible for package protection.
     */
    coverageEnabled: boolean;
    packageProtectionPlusEnabled: boolean;
    percentage: number;
    splitProducts: boolean;
    pricingRuleSets?: PricingRuleSet[];
    minPrice: number;
    maxPrice: number;
    minCoverageEligibleCartTotal?: number;
    maxCoverageEligibleCartTotal?: number;
    fixedSubscriptionPrice?: number;
    coverageMlModel?: CoverageMlModelDb;
    shouldRecordPackageProtectionExposure?: boolean;
  };
  predictedCountry?: string;
  customJs?: string;
  customPdpCss?: string;
  customToggleCss?: string;
  treatments?: Jsonified<TreatmentDoc>[] | undefined;
  forceIPAddressLocation?: boolean;
  discountCodePartialMatch?: boolean;
  excludePickup: boolean;
  dynamicReturnsPricing?: DynamicReturnPricing | null;
  preProtectedOrdersEnabled?: boolean;
  checkoutUICustomizations?: CheckoutUICustomizationSettings;
  internationalReturnsPricing?: InternationalReturnPricing | null;
  automation?: Automation;
  currencyExchangeRate?: number;
  finalSaleReturns: {
    /**
     * This boolean represents whether we should sell final sale return item coverage
     * on items that are eligible for final sale return and also sell label coverage
     * to carts with final sale return items.
     */
    coverageEnabled: boolean;
    coverageValidTags: string[];
    coverageValidCollections: string[];
    coverageValidCollectionTitles?: string[];
    percentage: number;
    pricingRuleSets?: PricingRuleSet[];
    minPrice?: number;
    maxPrice: number;
    shippingLabelCoveragePrice?: number;
    allCovered: boolean;
    allEligible: boolean;
    eligibleDiscountThreshold?: number | null | undefined;
  };
  customerAccounts: {
    enabled: boolean;
    saveForLaterPanel: {
      enabled: boolean;
      numViewedProductsUntilPanelOpen: number | undefined;
      title: string | undefined;
      message: string | undefined;
      anchorLocation: SaveForLaterSlideInPanelAnchorLocation | undefined;
    };
  };
  showIcons?: boolean;
  customerWidgetEnabled: boolean;
  reviews: { enabled: boolean };
  pricePerOrder?: string;
  brandKit: BrandKit;
  extendedWarranties?: {
    enabled: boolean;
    cartToggleExperience: ExtendedWarrantyCartToggleExperience;
    pdpRenderMethod: ExtendedWarrantyPdpRenderMethod;
    popupExperience: ExtendedWarrantyPopupExperience;
  };
  concierge: {
    enabled: boolean;
    placeConciergeProductForm: boolean;
    conciergeProductFormSettings?: ConciergeProductFormSettings | undefined;
  };
  marketing: {
    enabled: boolean;
    subscribeFromSaveForLater: boolean;
    numEmbeddedFormsConfigured?: number;
    backInStockButtonSelector?: string;
    backInStockInsertionMode?: BackInStockInsertionMode;
  };
  conversion: ConversionAutocheck;
  loyalty: LoyaltyAutocheck;
  termsAndConditions?: TermsAndConditions;
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
  addProductOnOptInClick?: boolean;
  useBuyNowCoverage?: boolean;
  buyNow?: { customCss?: string };
  tryFirst?: {
    sellingPlanId?: string;
    sellingPlanGroupId?: string;
    widget?: TnplWidgetSettings;
    template?: TnplContractTemplateInAutoCheck;
    useMinutesForTrialPeriod?: boolean;
  };
  cartRewards?: {
    threshold: number;
    type: string;
    approachingThresholdMessage?: string;
    pastThresholdMessage?: string;
    placementSelector?: string;
    enabled?: boolean;
  };
  cartTotals?: {
    placementSelector?: string;
    enabled?: boolean;
    customCss?: string;
  };
  cartOptimization?: CartOptimizationSettings;
  autoNavigateToCheckoutAfterTimeout?: boolean;
  shouldNotUseStorefrontApi?: boolean;
  disableBusinessAccountInStorefront?: boolean;
  preventPageRefreshAfterCartAdd?: boolean;
  dispatchResizeAfterItemsPlaced?: boolean;
  coverageCreditIncentive?: CoverageCreditIncentive;
  discountsAreFinalSales?: boolean;
  couponedItemsDiscountThreshold?: number;
  purpleDotEnabled?: boolean;
  billing: ShopperFacingBillingInfo;
  localizationGroups?: {
    _id: string;
    name: string;
    countries: string[];
    languages: string[];
  }[];
  localizationTextOverrides?: {
    localizationGroupId: string;
    localizationObjectId: string;
    path: string;
    text: string;
  }[];
  storefrontLoggingAllowedPercentage?: number;
  localizationObjectId: string;
}

export function getAutocheckLocalizationId(teamId: string) {
  // WARNING: if you edit this function you must perform a database migration to update the localizationId field for all autochecks.
  return `autocheck-${teamId}`;
}

export const showCoverageBasedOnBillingStatus: Record<
  ShopperFacingBillingStatus,
  boolean
> = {
  [ShopperFacingBillingStatus.UP_TO_DATE]: true,
  [ShopperFacingBillingStatus.MISSING_BILLING_INFO]: false,
  [ShopperFacingBillingStatus.BILLING_FAILURE]: false,
};

export const defaultTermsAndConditionsBlockedMessage =
  "Please agree to the terms and conditions before checking out";

export const sellingPlanGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  sellingPlans: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      billingPolicy: z.object({
        interval: z.string().optional(),
        intervalCount: z.number().optional(),
        intervalDays: z.number().optional(),
      }),
    }),
  ),
});
export type SellingPlanGroup = z.infer<typeof sellingPlanGroupSchema>;

export interface feeProduct {
  productId: number;
  variants: { variant_id: string; price: number }[];
  handle: string;
  type: Exclude<CartProductType, CartProductType.ExtendedWarranty>;
  sellingPlanGroups: SellingPlanGroup[];
}

export interface WidgetFees {
  price: string;
  handle: string;
  enabled: boolean;
  productId: number;
  variantId: number;
  mainType: Exclude<CartProductType, CartProductType.ExtendedWarranty>;
  splitProducts: boolean;
  returnProduct?: feeProduct;
  packageProduct?: feeProduct;
  bothProduct?: feeProduct;
}

export interface GetWidgetResponse {
  widget_slug: string;
  name: string;
  storeUrl: string;
  theme: Portal;
  settings: Settings;
  service_active: boolean;
  service_disabled_reason: string | null;
  credit_value: number | null;
  storefrontAccessToken: string | null;
  headlessAccessToken: string | undefined;
  hasStorefrontAccess: boolean;
  customerEmail: string;
  automation: Automation;
  address?: Address;
  customInStoreAddresses?: Address[];
}

export const defaultInfoCardWidget: InfoCard = {
  elementType: WidgetElementType.INFO_CARD,
  placementType: PlacementType.CART,
  cardText: undefined,
  showInfoIcon: true,
  placementSelector: "",
};

export const defaultBothCheckoutButtonsWidget: BothCheckoutButtons = {
  elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS,
  placementType: PlacementType.CART,
  placementSelector: "",
  coverageButtonText: undefined,
  nonCoverageButtonText: undefined,
  coverageButtonStyles: undefined,
};

export const defaultWidgets: WidgetConfig[] = [
  defaultInfoCardWidget,
  defaultBothCheckoutButtonsWidget,
];

const BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR =
  ".cart-actions, .previewCartCheckout, .previewCartAction";

const BIGCOMMERCE_TOGGLE_SELECTOR =
  ".optimizedCheckout-orderSummary-cartSection:nth-of-type(3)";

export const bigcommerceDefaultInfoCardWidget: InfoCard = {
  elementType: WidgetElementType.INFO_CARD,
  placementType: PlacementType.CART,
  insertionPosition: InsertionPosition.PREPEND,
  placementSelector: BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR,
  cardText: undefined,
  showInfoIcon: true,
};

export const bigcommerceDefaultBothCheckoutButtonsWidget: BothCheckoutButtons =
  {
    elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS,
    placementType: PlacementType.CART,
    insertionPosition: InsertionPosition.PREPEND,
    placementSelector: BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR,
    coverageButtonText: undefined,
    nonCoverageButtonText: undefined,
    coverageButtonStyles: undefined,
  };

export const bigcommerceDefaultToggleWidget: SingleToggleWidgetConfig = {
  elementType: WidgetElementType.SINGLE_TOGGLE,
  placementType: PlacementType.CART,
  insertionPosition: InsertionPosition.APPEND,
  placementSelector: BIGCOMMERCE_TOGGLE_SELECTOR,
};

export const bigcommerceDefaultWidgets: WidgetConfig[] = [
  bigcommerceDefaultInfoCardWidget,
  bigcommerceDefaultBothCheckoutButtonsWidget,
  bigcommerceDefaultToggleWidget,
];

export const isCheckoutButtonsOrInfoCards = (widgetConfig?: WidgetConfig) => {
  return (
    widgetConfig &&
    [
      WidgetElementType.COVERAGE_CHECKOUT_BUTTON,
      WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON,
      WidgetElementType.BOTH_CHECKOUT_BUTTONS,
      WidgetElementType.INFO_CARD,
    ].includes(widgetConfig.elementType)
  );
};
